import { Document, Packer, Paragraph, TextRun, Table, TableRow, TableCell, WidthType, AlignmentType, HeadingLevel, ImageRun } from 'docx';
import { saveAs } from 'file-saver';

const base64ToArrayBuffer = (base64) => {
  try {
    const binaryString = window.atob(base64.split(',')[1]);
    const len = binaryString.length;
    const bytes = new Uint8Array(len);
    for (let i = 0; i < len; i++) {
      bytes[i] = binaryString.charCodeAt(i);
    }
    return bytes.buffer;
  } catch (e) {
    console.error('Image conversion error:', e);
    return null;
  }
};

export const generateComparisonWord = async (comparisonData, results, purpose) => {
  try {
    const children = [];
    
    // Title
    children.push(
      new Paragraph({ text: 'EmoQ.ai', heading: HeadingLevel.HEADING_1, alignment: AlignmentType.CENTER, spacing: { after: 100 } }),
      new Paragraph({ text: 'Image Comparison Report', heading: HeadingLevel.HEADING_2, alignment: AlignmentType.CENTER, spacing: { after: 400 } })
    );
    
    // Purpose
    if (purpose) {
      children.push(
        new Paragraph({
          children: [new TextRun({ text: 'Purpose: ', bold: true, size: 26 }), new TextRun({ text: purpose, size: 24 })],
          spacing: { after: 400 },
          shading: { fill: "EEF2FF" }
        })
      );
    }
    
    // Winner Section
    children.push(
      new Paragraph({ text: 'WINNER', heading: HeadingLevel.HEADING_1, alignment: AlignmentType.CENTER, spacing: { before: 200, after: 200 } }),
      new Paragraph({ text: 'Image #' + (results[0].imageIndex + 1), heading: HeadingLevel.HEADING_2, alignment: AlignmentType.CENTER, spacing: { after: 200 } })
    );
    
    // Winner image
    const winnerImg = base64ToArrayBuffer(results[0].preview || results[0].image_data);
    if (winnerImg) {
      children.push(new Paragraph({ 
        children: [new ImageRun({ 
          data: winnerImg, 
          transformation: { width: 400, height: 300 } 
        })], 
        alignment: AlignmentType.CENTER, 
        spacing: { after: 300 } 
      }));
    }
    
    // Winner scores with colors
    children.push(
      new Table({
        width: { size: 70, type: WidthType.PERCENTAGE },
        rows: [
          new TableRow({ children: [new TableCell({ children: [new Paragraph({ text: 'Overall Score', bold: true })], shading: { fill: "FEF3C7" } }), new TableCell({ children: [new Paragraph({ text: results[0].overallScore.toFixed(1) + '/100', bold: true })], shading: { fill: "FEF3C7" } })] }),
          new TableRow({ children: [new TableCell({ children: [new Paragraph('Valence (Positivity)')], shading: { fill: "D1FAE5" } }), new TableCell({ children: [new Paragraph(((results[0].valence || 0) * 100).toFixed(0) + '%')] })] }),
          new TableRow({ children: [new TableCell({ children: [new Paragraph('Arousal (Energy)')], shading: { fill: "FFEDD5" } }), new TableCell({ children: [new Paragraph(((results[0].arousal_level || 0) * 100).toFixed(0) + '%')] })] }),
          new TableRow({ children: [new TableCell({ children: [new Paragraph('Dominance')], shading: { fill: "E0E7FF" } }), new TableCell({ children: [new Paragraph(((results[0].dominance || 0) * 100).toFixed(0) + '%')] })] }),
          new TableRow({ children: [new TableCell({ children: [new Paragraph('Emotion')], shading: { fill: "FCE7F3" } }), new TableCell({ children: [new Paragraph(results[0].dominant_emotion)] })] })
        ]
      })
    );
    
    if (results[0].custom_answer) {
      children.push(
        new Paragraph({ text: 'Why This Image Wins', heading: HeadingLevel.HEADING_2, spacing: { before: 300, after: 200 } }),
        new Paragraph({ text: String(results[0].custom_answer), spacing: { after: 600 }, shading: { fill: "F3F4F6" } })
      );
    }
    
    // ALL IMAGES
    children.push(
      new Paragraph({ text: '', pageBreakBefore: true }),
      new Paragraph({ text: 'ALL COMPARED IMAGES', heading: HeadingLevel.HEADING_1, alignment: AlignmentType.CENTER, spacing: { after: 400 } })
    );
    
    for (let i = 0; i < results.length; i++) {
      const result = results[i];
      const isWinner = i === 0;
      
      children.push(
        new Paragraph({ text: `${isWinner ? '🏆 ' : ''}#${i + 1} - Image ${result.imageIndex + 1}`, heading: HeadingLevel.HEADING_2, spacing: { before: 400, after: 200 }, shading: { fill: isWinner ? "FEF3C7" : "F3F4F6" } })
      );
      
      const img = base64ToArrayBuffer(result.preview || result.image_data);
      if (img) {
        children.push(new Paragraph({ 
          children: [new ImageRun({ 
            data: img, 
            transformation: { width: 350, height: 260 } 
          })], 
          alignment: AlignmentType.CENTER, 
          spacing: { after: 200 } 
        }));
      }
      
      children.push(
        new Table({
          width: { size: 70, type: WidthType.PERCENTAGE },
          rows: [
            new TableRow({ children: [new TableCell({ children: [new Paragraph({ text: 'Metric', bold: true })], shading: { fill: "DBEAFE" } }), new TableCell({ children: [new Paragraph({ text: 'Value', bold: true })], shading: { fill: "DBEAFE" } })] }),
            new TableRow({ children: [new TableCell({ children: [new Paragraph('Score')] }), new TableCell({ children: [new Paragraph(result.overallScore.toFixed(1) + '/100')] })] }),
            new TableRow({ children: [new TableCell({ children: [new Paragraph('Emotion')] }), new TableCell({ children: [new Paragraph(result.dominant_emotion)] })] }),
            new TableRow({ children: [new TableCell({ children: [new Paragraph('Valence')], shading: { fill: "D1FAE5" } }), new TableCell({ children: [new Paragraph(((result.valence || 0) * 100).toFixed(0) + '%')] })] }),
            new TableRow({ children: [new TableCell({ children: [new Paragraph('Arousal')], shading: { fill: "FFEDD5" } }), new TableCell({ children: [new Paragraph(((result.arousal_level || 0) * 100).toFixed(0) + '%')] })] }),
            new TableRow({ children: [new TableCell({ children: [new Paragraph('Dominance')], shading: { fill: "E0E7FF" } }), new TableCell({ children: [new Paragraph(((result.dominance || 0) * 100).toFixed(0) + '%')] })] })
          ]
        })
      );
      
      if (result.custom_answer) {
        children.push(new Paragraph({ text: 'Analysis:', spacing: { before: 200, after: 100 } }), new Paragraph({ text: String(result.custom_answer), spacing: { after: 300 }, shading: { fill: "F3F4F6" } }));
      }
      
      children.push(new Paragraph({ text: 'Demographics:', spacing: { before: 200, after: 100 } }));
      Object.entries(result.audience_reactions || {}).forEach(([group, reaction]) => {
        children.push(new Paragraph({ children: [new TextRun({ text: group + ': ', bold: true }), new TextRun({ text: String(reaction) })], spacing: { after: 100 } }));
      });
      
      // Regional Data
      children.push(new Paragraph({ text: 'Regional Performance:', spacing: { before: 200, after: 100 } }));
      children.push(
        new Table({
          width: { size: 70, type: WidthType.PERCENTAGE },
          rows: [
            new TableRow({ 
              children: [
                new TableCell({ children: [new Paragraph({ text: 'Region', bold: true })], shading: { fill: "DBEAFE" } }), 
                new TableCell({ children: [new Paragraph({ text: 'Score', bold: true })], shading: { fill: "DBEAFE" } })
              ] 
            }),
            new TableRow({ 
              children: [
                new TableCell({ children: [new Paragraph('North America')], shading: { fill: "DBEAFE" } }), 
                new TableCell({ children: [new Paragraph(((result.valence || 0) * 100).toFixed(0) + '%')] })
              ] 
            }),
            new TableRow({ 
              children: [
                new TableCell({ children: [new Paragraph('Europe')], shading: { fill: "D1FAE5" } }), 
                new TableCell({ children: [new Paragraph((((result.valence || 0) + (result.arousal_level || 0)) * 50).toFixed(0) + '%')] })
              ] 
            }),
            new TableRow({ 
              children: [
                new TableCell({ children: [new Paragraph('Asia')], shading: { fill: "EDE9FE" } }), 
                new TableCell({ children: [new Paragraph(((result.arousal_level || 0) * 100).toFixed(0) + '%')] })
              ] 
            }),
            new TableRow({ 
              children: [
                new TableCell({ children: [new Paragraph('Latin America')], shading: { fill: "FFEDD5" } }), 
                new TableCell({ children: [new Paragraph((((result.valence || 0) + (result.dominance || 0)) * 50).toFixed(0) + '%')] })
              ] 
            }),
            new TableRow({ 
              children: [
                new TableCell({ children: [new Paragraph('Middle East')], shading: { fill: "FCE7F3" } }), 
                new TableCell({ children: [new Paragraph(((result.dominance || 0) * 100).toFixed(0) + '%')] })
              ] 
            }),
            new TableRow({ 
              children: [
                new TableCell({ children: [new Paragraph('Africa')], shading: { fill: "CCFBF1" } }), 
                new TableCell({ children: [new Paragraph((((result.arousal_level || 0) + (result.dominance || 0)) * 50).toFixed(0) + '%')] })
              ] 
            })
          ]
        })
      );
      
      children.push(new Paragraph({ text: 'Recommendations:', spacing: { before: 200, after: 100 } }));
      (result.recommendations || []).forEach((rec, idx) => {
        children.push(new Paragraph({ text: `${idx + 1}. ${String(rec)}`, spacing: { after: 50 } }));
      });
      
      if (i < results.length - 1) children.push(new Paragraph({ text: '', pageBreakBefore: true }));
    }
    
    children.push(new Paragraph({ text: '', pageBreakBefore: true }), new Paragraph({ text: 'Generated by EmoQ.ai', alignment: AlignmentType.CENTER }));
    
    const doc = new Document({ sections: [{ children }] });
    const blob = await Packer.toBlob(doc);
    saveAs(blob, 'emoQ-comparison-' + Date.now() + '.docx');
    return true;
  } catch (error) {
    console.error('Word error:', error);
    throw error;
  }
};

export const generateSingleAnalysisWord = async (analysis) => {
  console.log('generateSingleAnalysisWord called with:', analysis);
  try {
    console.log('Creating document sections...');
    const children = [
      new Paragraph({ text: 'EmoQ.ai', heading: HeadingLevel.HEADING_1, alignment: AlignmentType.CENTER, spacing: { after: 100 } }),
      new Paragraph({ text: 'Analysis Report', heading: HeadingLevel.HEADING_2, alignment: AlignmentType.CENTER, spacing: { after: 400 } }),
      new Paragraph({ children: [new TextRun({ text: 'Type: ', bold: true }), new TextRun({ text: analysis.content_type?.toUpperCase() || 'ANALYSIS' })], spacing: { after: 200 } })
    ];
    
    if (analysis.timestamp) {
      try {
        const date = new Date(analysis.timestamp);
        children.push(new Paragraph({ children: [new TextRun({ text: 'Date: ', bold: true }), new TextRun({ text: date.toLocaleString() })], spacing: { after: 300 } }));
      } catch (e) {}
    }
    
    if (analysis.custom_question) {
      children.push(new Paragraph({ text: 'Your Question:', spacing: { before: 200, after: 100 } }), new Paragraph({ text: analysis.custom_question, spacing: { after: 300 } }));
    }
    
    children.push(new Paragraph({ text: 'ORIGINAL CONTENT', heading: HeadingLevel.HEADING_2, spacing: { before: 400, after: 200 } }));
    
    if ((analysis.content_type === 'image' || analysis.content_type === 'live') && analysis.image_data) {
      const img = base64ToArrayBuffer(analysis.image_data);
      if (img) {
        children.push(new Paragraph({ 
          children: [new ImageRun({ 
            data: img, 
            transformation: { width: 450, height: 340 } 
          })], 
          alignment: AlignmentType.CENTER, 
          spacing: { after: 300 } 
        }));
      }
    } else if (analysis.content_type === 'text') {
      children.push(new Paragraph({ text: 'Text:', spacing: { after: 100 } }), new Paragraph({ text: analysis.content_preview || analysis.content || 'N/A', spacing: { after: 300 } }));
    } else if (analysis.content_type === 'url') {
      children.push(new Paragraph({ text: 'URL:', spacing: { after: 100 } }), new Paragraph({ text: analysis.content || 'N/A', spacing: { after: 200 } }));
      if (analysis.content_description) {
        children.push(new Paragraph({ text: 'Description:', spacing: { after: 100 } }), new Paragraph({ text: analysis.content_description, spacing: { after: 300 } }));
      }
    }
    
    children.push(
      new Paragraph({ text: 'KEY METRICS', heading: HeadingLevel.HEADING_2, spacing: { before: 400, after: 200 } }),
      new Paragraph({ text: 'Emotion: ' + analysis.dominant_emotion, spacing: { after: 100 } }),
      new Paragraph({ text: 'Valence: ' + Math.round(analysis.valence * 100) + '%', spacing: { after: 100 } }),
      new Paragraph({ text: 'Arousal: ' + Math.round(analysis.arousal_level * 100) + '%', spacing: { after: 100 } }),
      new Paragraph({ text: 'Dominance: ' + Math.round(analysis.dominance * 100) + '%', spacing: { after: 300 } })
    );
    
    if (analysis.custom_answer) {
      children.push(new Paragraph({ text: 'AI ANALYSIS', heading: HeadingLevel.HEADING_2, spacing: { before: 400, after: 200 } }), new Paragraph({ text: String(analysis.custom_answer), spacing: { after: 400 }, shading: { fill: "F3F4F6" } }));
    }
    
    children.push(new Paragraph({ text: '', pageBreakBefore: true }), new Paragraph({ text: 'DEMOGRAPHICS', heading: HeadingLevel.HEADING_2, spacing: { after: 200 } }));
    Object.entries(analysis.audience_reactions || {}).forEach(([group, reaction]) => {
      children.push(new Paragraph({ children: [new TextRun({ text: group + ': ', bold: true }), new TextRun({ text: String(reaction) })], spacing: { after: 100 } }));
    });
    
    // GLOBAL EMOTIONAL RESPONSE
    console.log('Checking global_emotional_response:', analysis.global_emotional_response);
    if (analysis.global_emotional_response && Object.keys(analysis.global_emotional_response).length > 0) {
      console.log('Adding global_emotional_response to Word document');
      children.push(new Paragraph({ text: 'GLOBAL EMOTIONAL RESPONSE', heading: HeadingLevel.HEADING_2, spacing: { before: 400, after: 200 } }));
      Object.entries(analysis.global_emotional_response).forEach(([region, data]) => {
        if (data && typeof data === 'object' && data.response && data.percentage !== undefined) {
          children.push(
            new Paragraph({ text: `${region}: ${data.response} (${data.percentage}%)`, spacing: { before: 100, after: 100 }, shading: { fill: "D1FAE5" } })
          );
        }
      });
      // Calculate and add global average
      try {
        const percentages = Object.values(analysis.global_emotional_response)
          .filter(d => d && d.percentage !== undefined)
          .map(d => d.percentage);
        if (percentages.length > 0) {
          const average = Math.round(percentages.reduce((a, b) => a + b, 0) / percentages.length);
          children.push(new Paragraph({ text: `Global Average: ${average}%`, bold: true, spacing: { before: 200, after: 100 }, shading: { fill: "A7F3D0" } }));
        }
      } catch (e) {
        console.error('Error calculating global average:', e);
      }
    } else {
      console.log('No global_emotional_response data to add to Word document');
    }
    
    children.push(new Paragraph({ text: 'EMOTIONS', heading: HeadingLevel.HEADING_2, spacing: { before: 400, after: 200 } }));
    Object.entries(analysis.emotions || {}).sort(([,a], [,b]) => b - a).forEach(([emotion, value]) => {
      children.push(new Paragraph({ text: emotion.charAt(0).toUpperCase() + emotion.slice(1) + ': ' + Math.round(value) + '%', spacing: { after: 50 } }));
    });
    
    // ADVANCED FEATURES
    if (analysis.psychographics) {
      children.push(new Paragraph({ text: 'PSYCHOGRAPHIC PROFILE', heading: HeadingLevel.HEADING_2, spacing: { before: 400, after: 200 } }));
      if (analysis.psychographics.values?.length) {
        children.push(new Paragraph({ text: 'Values: ' + analysis.psychographics.values.join(', '), spacing: { after: 100 } }));
      }
      if (analysis.psychographics.interests?.length) {
        children.push(new Paragraph({ text: 'Interests: ' + analysis.psychographics.interests.join(', '), spacing: { after: 100 } }));
      }
      if (analysis.psychographics.motivations?.length) {
        children.push(new Paragraph({ text: 'Motivations: ' + analysis.psychographics.motivations.join(', '), spacing: { after: 100 } }));
      }
    }
    
    if (analysis.cultural_intelligence) {
      children.push(new Paragraph({ text: 'CULTURAL INTELLIGENCE', heading: HeadingLevel.HEADING_2, spacing: { before: 400, after: 200 } }));
      Object.entries(analysis.cultural_intelligence).forEach(([region, data]) => {
        if (data && typeof data === 'object' && (data.sensitivity_score !== undefined || data.sensitivity !== undefined)) {
          children.push(
            new Paragraph({ text: region, bold: true, spacing: { before: 200, after: 100 }, shading: { fill: "D1FAE5" } }),
            new Paragraph({ text: `Sensitivity Score: ${data.sensitivity_score || data.sensitivity}/100`, spacing: { after: 50 } }),
            new Paragraph({ text: `Cultural Fit: ${data.cultural_fit}`, spacing: { after: 50 } })
          );
          if (data.language_tone) {
            children.push(new Paragraph({ text: `Language Tone: ${data.language_tone}`, spacing: { after: 50 } }));
          }
          if (data.cultural_notes) {
            children.push(new Paragraph({ text: `Cultural Notes: ${data.cultural_notes}`, spacing: { after: 50 } }));
          }
          if (data.warnings) {
            children.push(new Paragraph({ text: `⚠️ Warning: ${data.warnings}`, spacing: { after: 50 }, shading: { fill: "FEE2E2" } }));
          }
          if (data.localization_tips) {
            children.push(new Paragraph({ text: `💡 Localization Tip: ${data.localization_tips}`, spacing: { after: 100 }, shading: { fill: "DBEAFE" } }));
          }
        }
      });
    }
    
    if (analysis.platform_recommendations) {
      children.push(new Paragraph({ text: 'PLATFORM RECOMMENDATIONS', heading: HeadingLevel.HEADING_2, spacing: { before: 400, after: 200 } }));
      Object.entries(analysis.platform_recommendations).forEach(([platform, data]) => {
        children.push(
          new Paragraph({ text: `${platform}: ${data.score}/100`, bold: true, spacing: { before: 200, after: 50 }, shading: { fill: "F3E8FF" } })
        );
        if (data.reasoning) {
          children.push(new Paragraph({ text: `Why: ${data.reasoning}`, spacing: { after: 50 } }));
        }
        if (data.best_time) {
          children.push(new Paragraph({ text: `⏰ Best Time to Post: ${data.best_time}`, spacing: { after: 50 }, shading: { fill: "E0E7FF" } }));
        }
        if (data.best_practices && Array.isArray(data.best_practices)) {
          children.push(new Paragraph({ text: 'Best Practices:', bold: true, spacing: { after: 50 } }));
          data.best_practices.forEach((practice, idx) => {
            children.push(new Paragraph({ text: `  ${idx + 1}. ${practice}`, spacing: { after: 30 } }));
          });
        }
        if (data.content_tips) {
          children.push(new Paragraph({ text: `💡 Content Tips: ${data.content_tips}`, spacing: { after: 100 }, shading: { fill: "FEF3C7" } }));
        }
        // Legacy fields support
        if (data.format) {
          children.push(new Paragraph({ text: `Recommended Format: ${data.format}`, spacing: { after: 50 } }));
        }
        if (data.tips) {
          children.push(new Paragraph({ text: `Tips: ${data.tips}`, spacing: { after: 100 } }));
        }
      });
    }
    
    if (analysis.ab_testing_recommendations) {
      children.push(new Paragraph({ text: 'A/B TESTING RECOMMENDATIONS', heading: HeadingLevel.HEADING_2, spacing: { before: 400, after: 200 } }));
      Object.entries(analysis.ab_testing_recommendations).forEach(([key, data]) => {
        if ((key.startsWith('test_') || key.startsWith('variation')) && data) {
          children.push(
            new Paragraph({ text: `${data.element || key.replace('_', ' ')}`.toUpperCase(), bold: true, spacing: { before: 200, after: 50 }, shading: { fill: "DBEAFE" } })
          );
          if (data.priority) {
            children.push(new Paragraph({ text: `Priority: ${data.priority.toUpperCase()}`, spacing: { after: 50 } }));
          }
          if (data.variation_a) {
            children.push(new Paragraph({ text: `Variation A: ${data.variation_a}`, spacing: { after: 50 } }));
          }
          if (data.variation_b) {
            children.push(new Paragraph({ text: `Variation B: ${data.variation_b}`, spacing: { after: 50 } }));
          }
          if (data.hypothesis) {
            children.push(new Paragraph({ text: `Hypothesis: ${data.hypothesis}`, spacing: { after: 50 } }));
          }
          if (data.expected_impact) {
            children.push(new Paragraph({ text: `Expected Impact: ${data.expected_impact}`, spacing: { after: 50 } }));
          }
          if (data.test_duration) {
            children.push(new Paragraph({ text: `Test Duration: ${data.test_duration}`, spacing: { after: 50 } }));
          }
          if (data.sample_size) {
            children.push(new Paragraph({ text: `Sample Size: ${data.sample_size}`, spacing: { after: 50 } }));
          }
          if (data.success_metrics && Array.isArray(data.success_metrics)) {
            children.push(new Paragraph({ text: `Success Metrics: ${data.success_metrics.join(', ')}`, spacing: { after: 100 } }));
          }
          // Legacy support
          if (data.change) {
            children.push(new Paragraph({ text: `Change: ${data.change}`, spacing: { after: 50 } }));
          }
        }
      });
    }
    
    children.push(new Paragraph({ text: 'RECOMMENDATIONS', heading: HeadingLevel.HEADING_2, spacing: { before: 400, after: 200 } }));
    (analysis.recommendations || []).forEach((rec, idx) => {
      children.push(new Paragraph({ text: `${idx + 1}. ${rec}`, spacing: { after: 100 } }));
    });
    
    // EMOQ CONTENT ENHANCEMENT
    if (analysis.enhancement) {
      const enhancement = analysis.enhancement;
      
      children.push(new Paragraph({ text: 'EMOQ CONTENT ENHANCEMENT', heading: HeadingLevel.HEADING_2, spacing: { before: 600, after: 300 }, shading: { fill: "E9D5FF" } }));
      
      // Enhanced Content
      if (enhancement.enhanced_content) {
        children.push(new Paragraph({ text: 'Enhanced Content:', bold: true, spacing: { before: 200, after: 100 } }));
        children.push(new Paragraph({ text: enhancement.enhanced_content, spacing: { after: 300 } }));
      }
      
      // Show both original and annotated images
      if (analysis.image_data) {
        children.push(new Paragraph({ text: 'Visual Comparison:', bold: true, spacing: { before: 300, after: 100 } }));
        
        // Original Image
        children.push(new Paragraph({ text: 'Original Image:', spacing: { after: 50 } }));
        const originalImg = base64ToArrayBuffer(analysis.image_data);
        if (originalImg) {
          children.push(new Paragraph({ 
            children: [new ImageRun({ 
              data: originalImg, 
              transformation: { width: 400, height: 300 } 
            })], 
            alignment: AlignmentType.CENTER, 
            spacing: { after: 200 } 
          }));
        }
        
        // Annotated Image
        if (enhancement.annotated_image || analysis.annotated_image) {
          children.push(new Paragraph({ text: 'Annotated Image with Suggestions:', spacing: { after: 50 } }));
          const annotatedImg = base64ToArrayBuffer(enhancement.annotated_image || analysis.annotated_image);
          if (annotatedImg) {
            children.push(new Paragraph({ 
              children: [new ImageRun({ 
                data: annotatedImg, 
                transformation: { width: 400, height: 300 } 
              })], 
              alignment: AlignmentType.CENTER, 
              spacing: { after: 300 } 
            }));
          }
        }
      }
      
      // Strategic Recommendations
      if (enhancement.suggestions && enhancement.suggestions.length > 0) {
        children.push(new Paragraph({ text: 'Strategic Recommendations:', bold: true, spacing: { before: 300, after: 100 } }));
        enhancement.suggestions.forEach((suggestion, idx) => {
          children.push(new Paragraph({ text: `${idx + 1}. ${suggestion}`, spacing: { after: 100 } }));
        });
      }
      
      // Platform Tips
      if (enhancement.platform_tips && Object.keys(enhancement.platform_tips).length > 0) {
        children.push(new Paragraph({ text: 'Platform-Specific Optimization:', bold: true, spacing: { before: 300, after: 100 } }));
        Object.entries(enhancement.platform_tips).forEach(([platform, tip]) => {
          children.push(new Paragraph({ text: `${platform}: ${tip}`, spacing: { after: 100 } }));
        });
      }
      
      // Predicted Improvements
      if (enhancement.predicted_scores && Object.keys(enhancement.predicted_scores).length > 0) {
        children.push(new Paragraph({ text: 'Predicted Emotional Improvements:', bold: true, spacing: { before: 300, after: 100 } }));
        Object.entries(enhancement.predicted_scores).forEach(([emotion, score]) => {
          const original = analysis.emotions[emotion] || 0;
          const improvement = score - original;
          if (improvement > 0) {
            children.push(new Paragraph({ 
              text: `${emotion.charAt(0).toUpperCase() + emotion.slice(1)}: ${Math.round(original)}% → ${Math.round(score)}% (+${Math.round(improvement)}%)`, 
              spacing: { after: 50 } 
            }));
          }
        });
      }
    }
    
    // SUPER ENHANCER FEATURES
    console.log('Checking super enhancement features...');
    
    // AI Creative (Image Generation)
    if (analysis.super_enhancement_creative) {
      console.log('Adding AI Creative section');
      children.push(
        new Paragraph({ text: 'EMOQ AI CREATIVE', heading: HeadingLevel.HEADING_2, spacing: { before: 600, after: 300 }, shading: { fill: "E9D5FF" } }),
        new Paragraph({ text: 'AI-Generated Image Enhancement', spacing: { after: 200 } })
      );
      
      if (analysis.super_enhancement_creative.prompt) {
        children.push(
          new Paragraph({ text: 'Generation Prompt:', bold: true, spacing: { after: 100 } }),
          new Paragraph({ text: analysis.super_enhancement_creative.prompt, spacing: { after: 200 } })
        );
      }
      
      if (analysis.super_enhancement_creative.generated_image) {
        try {
          const img = base64ToArrayBuffer(analysis.super_enhancement_creative.generated_image);
          if (img) {
            children.push(
              new Paragraph({ text: 'AI-Generated Image:', bold: true, spacing: { after: 100 } }),
              new Paragraph({ 
                children: [new ImageRun({ 
                  data: img, 
                  transformation: { width: 450, height: 340 } 
                })], 
                alignment: AlignmentType.CENTER, 
                spacing: { after: 300 } 
              })
            );
          }
        } catch (e) {
          console.error('Error adding AI creative image:', e);
        }
      }
    }
    
    // Photo Enhancement
    if (analysis.super_enhancement_photo) {
      console.log('Adding Photo Enhancement section');
      children.push(
        new Paragraph({ text: 'PHOTO ENHANCEMENT', heading: HeadingLevel.HEADING_2, spacing: { before: 600, after: 300 }, shading: { fill: "DBEAFE" } }),
        new Paragraph({ text: 'Step-by-Step Enhancement Instructions', spacing: { after: 200 } })
      );
      
      if (analysis.super_enhancement_photo.instructions) {
        children.push(
          new Paragraph({ text: 'Enhancement Instructions:', bold: true, spacing: { after: 100 } }),
          new Paragraph({ text: analysis.super_enhancement_photo.instructions, spacing: { after: 200 } })
        );
      }
      
      ['iphone_steps', 'android_steps', 'photoshop_steps'].forEach(platform => {
        if (analysis.super_enhancement_photo[platform]) {
          const platformName = platform.replace('_steps', '').toUpperCase();
          children.push(
            new Paragraph({ text: `${platformName} Instructions:`, bold: true, spacing: { before: 200, after: 100 } })
          );
          
          const steps = Array.isArray(analysis.super_enhancement_photo[platform]) 
            ? analysis.super_enhancement_photo[platform]
            : analysis.super_enhancement_photo[platform].split('\n').filter(s => s.trim());
            
          steps.forEach((step, idx) => {
            const cleanStep = step.replace(/^\d+[\.\)]\s*/, '').trim();
            if (cleanStep) {
              children.push(
                new Paragraph({ text: `  ${idx + 1}. ${cleanStep}`, spacing: { after: 50 } })
              );
            }
          });
        }
      });
    }
    
    // Text Tone Optimizer
    if (analysis.super_enhancement_text_tone) {
      console.log('Adding Text Tone Optimizer section');
      children.push(
        new Paragraph({ text: 'TEXT TONE OPTIMIZER', heading: HeadingLevel.HEADING_2, spacing: { before: 600, after: 300 }, shading: { fill: "FEF3C7" } }),
        new Paragraph({ text: 'Optimized Content Variations', spacing: { after: 200 } })
      );
      
      ['professional', 'persuasive', 'empathetic'].forEach(tone => {
        if (analysis.super_enhancement_text_tone[tone]) {
          children.push(
            new Paragraph({ text: `${tone.toUpperCase()} TONE:`, bold: true, spacing: { before: 300, after: 100 }, shading: { fill: "FDE68A" } }),
            new Paragraph({ text: analysis.super_enhancement_text_tone[tone], spacing: { after: 200 } })
          );
        }
      });
    }
    
    // Video Caption Generator
    if (analysis.super_enhancement_video_captions) {
      console.log('Adding Video Caption Generator section');
      children.push(
        new Paragraph({ text: 'VIDEO CAPTION GENERATOR', heading: HeadingLevel.HEADING_2, spacing: { before: 600, after: 300 }, shading: { fill: "DBEAFE" } }),
        new Paragraph({ text: 'Professional Captions & Subtitles', spacing: { after: 200 } })
      );
      
      ['minimal', 'engaging', 'dramatic'].forEach(style => {
        if (analysis.super_enhancement_video_captions[style]) {
          children.push(
            new Paragraph({ text: `${style.toUpperCase()} STYLE:`, bold: true, spacing: { before: 300, after: 100 }, shading: { fill: "93C5FD" } })
          );
          
          const captions = analysis.super_enhancement_video_captions[style];
          if (Array.isArray(captions)) {
            captions.forEach((caption, idx) => {
              if (caption.text) {
                children.push(
                  new Paragraph({ 
                    text: `[${caption.start_time}s - ${caption.end_time}s] ${caption.text}`, 
                    spacing: { after: 100 } 
                  })
                );
              }
            });
          } else if (typeof captions === 'string') {
            children.push(new Paragraph({ text: captions, spacing: { after: 200 } }));
          }
        }
      });
    }
    
    children.push(new Paragraph({ text: 'Generated by EmoQ.ai', alignment: AlignmentType.CENTER, spacing: { before: 600 } }));
    
    console.log('Creating Word document...');
    const doc = new Document({ sections: [{ children }] });
    console.log('Converting to blob...');
    const blob = await Packer.toBlob(doc);
    console.log('Saving file...');
    saveAs(blob, 'emoQ-analysis-' + Date.now() + '.docx');
    console.log('File saved successfully!');
    return true;
  } catch (error) {
    console.error('Word generation error:', error);
    console.error('Error stack:', error.stack);
    throw error;
  }
};
